// React Imports
import { useEffect, useState } from "react";

// External Libraries
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";

// Local Components
import { LoginLayout } from "../LoginLayout/LoginLayout";

// Hooks & Helpers
import { getLoginClient } from "sj-ui-toolkit/src/client";
import type { OAuthTenantChoice } from "sj-ui-toolkit/src/client/svcIdp/idpTypes";
import { themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";
import {
  RESTART_HINT,
  isInvalidArgument,
  isPermissionDenied,
  resolveHttpUrl,
} from "./mcpLoginHelpers";

// Unknown, expired and already-used selection ids are indistinguishable (all 400).
const INVALID_SELECTION_MESSAGE = `This sign-in link is invalid or has expired. ${RESTART_HINT}`;

type TenantChoice = { name: string; displayName: string };

const normalizeTenants = (choices: OAuthTenantChoice[] = []): TenantChoice[] =>
  choices.flatMap(choice =>
    choice.name ? [{ name: choice.name, displayName: choice.display_name || choice.name }] : []
  );

export function McpTenantSelection({ selectionId }: { selectionId: string }) {
  const loginClient = getLoginClient();
  const [tenants, setTenants] = useState<TenantChoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [submittingTenant, setSubmittingTenant] = useState<string | null>(null);
  const [terminalError, setTerminalError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadTenants = async () => {
      const { data, error } = await loginClient.getOAuthTenantSelection(selectionId);
      if (cancelled) return;

      if (error) {
        setTerminalError(
          isInvalidArgument(error)
            ? INVALID_SELECTION_MESSAGE
            : `Something went wrong while loading your tenants. ${RESTART_HINT}`
        );
      } else {
        const choices = normalizeTenants(data.tenants);
        if (choices.length === 0) {
          setTerminalError(`No tenants are available for this connection. ${RESTART_HINT}`);
        } else {
          setTenants(choices);
        }
      }
      setIsLoading(false);
    };
    loadTenants();

    return () => {
      cancelled = true;
    };
  }, [selectionId]);

  const handleSelectTenant = async (tenant: string) => {
    setSubmittingTenant(tenant);
    const { data, error } = await loginClient.selectOAuthTenant({
      selection_id: selectionId,
      tenant,
    });

    if (error) {
      if (isInvalidArgument(error)) {
        setTerminalError(INVALID_SELECTION_MESSAGE);
      } else if (isPermissionDenied(error)) {
        setTerminalError(`That tenant is not available for this connection. ${RESTART_HINT}`);
      } else {
        window.toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Unable to continue sign-in. Please try again.",
        });
        setSubmittingTenant(null);
      }
      return;
    }

    const href = data.redirect_url ? resolveHttpUrl(data.redirect_url) : null;
    if (!href) {
      setTerminalError(`Something went wrong while preparing sign-in. ${RESTART_HINT}`);
      return;
    }
    // Leave submittingTenant set so the button stays loading while navigating away.
    window.location.href = href;
  };

  return (
    <LoginLayout title={terminalError ? "Login" : "Select a Tenant to Continue"}>
      {terminalError ? (
        <div
          className="flex flex-col w-full gap-2"
          role="alert"
          data-testid="mcp-tenant-selection-error"
        >
          <div className="text-center">
            <p className="mb-4">{terminalError}</p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center w-full" role="status" aria-live="polite">
          <ProgressSpinner />
          <span className="sr-only">Loading tenants, please wait</span>
        </div>
      ) : (
        <div className="flex flex-col w-full gap-3" data-testid="mcp-tenant-selection">
          <Card className={T.bgElevation2}>
            <h2 id="mcp-tenant-heading" className="atlas-h4">
              Connect your MCP client
            </h2>
            <p>Select the tenant your MCP client will connect to.</p>
          </Card>
          <div className="flex flex-col gap-3" role="group" aria-labelledby="mcp-tenant-heading">
            {tenants.map(tenant => (
              <Button
                key={tenant.name}
                type="button"
                className="justify-start w-full text-left"
                data-testid={`mcp-select-tenant-button-${tenant.name}`}
                label={tenant.displayName}
                icon="pi pi-building"
                onClick={() => handleSelectTenant(tenant.name)}
                disabled={submittingTenant !== null}
                loading={submittingTenant === tenant.name}
              />
            ))}
          </div>
        </div>
      )}
    </LoginLayout>
  );
}
